/**
 * Universal GraphQL ↔ DB enum bridge.
 *
 * **Convention** (all entries in `$lib/enums/index.ts`):
 * - Object **keys** = GraphQL enum member names (SCREAMING_SNAKE) — sent in mutations/queries.
 * - Object **values** = backend `IntEnum` integers from `common.enums` (or same string for StrEnum).
 *
 * Form state and `<Select>` values should always hold GraphQL **names** (keys), not ints.
 * Use `parseGraphqlEnumName` when hydrating from the API and `toGraphqlEnumName` before mutations.
 */

import { formatEnum } from "$lib/utils/format";

/** CRM enum catalog entry — keys are GraphQL names, values are DB ints or legacy strings. */
export type GraphqlEnumDef = Record<string, string | number>;

export type GraphqlEnumName<T extends GraphqlEnumDef> = Extract<keyof T, string>;

function normalizeKey(raw: string): string {
  return raw.trim().toUpperCase().replace(/-/g, "_");
}

/** Coerce unknown values to a string safe for `.trim()` and DOM inputs. */
export function asEnumString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (value == null) return "";
  return String(value);
}

/** All GraphQL member names defined on `enumObj` (object keys). */
export function graphqlEnumNames<T extends GraphqlEnumDef>(enumObj: T): GraphqlEnumName<T>[] {
  return Object.keys(enumObj) as GraphqlEnumName<T>[];
}

/** DB / stored integer for a GraphQL member, when `enumObj` uses IntEnum values. */
export function graphqlEnumInt<T extends GraphqlEnumDef>(
  enumObj: T,
  name: GraphqlEnumName<T>,
): number | null {
  const value = enumObj[name];
  return typeof value === "number" ? value : null;
}

export function isGraphqlEnumName<T extends GraphqlEnumDef>(
  value: unknown,
  enumObj: T,
): value is GraphqlEnumName<T> {
  return typeof value === "string" && normalizeKey(value) in enumObj;
}

/**
 * Resolve a raw API / form / JWT value to the GraphQL member **name** (object key).
 * Accepts member name, numeric IntEnum value, or legacy string value equal to the name.
 */
export function parseGraphqlEnumName<T extends GraphqlEnumDef>(
  value: unknown,
  enumObj: T,
): GraphqlEnumName<T> | "" {
  if (value == null || value === "") return "";

  if (typeof value === "number" && Number.isFinite(value)) {
    for (const [name, stored] of Object.entries(enumObj)) {
      if (stored === value) return name as GraphqlEnumName<T>;
    }
    return "";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const key = normalizeKey(trimmed);
    if (key in enumObj) return key as GraphqlEnumName<T>;
    // Legacy objects where value === name string
    for (const [name, stored] of Object.entries(enumObj)) {
      if (typeof stored === "string" && normalizeKey(stored) === key) {
        return name as GraphqlEnumName<T>;
      }
    }
    return trimmed as GraphqlEnumName<T>;
  }

  return "";
}

/** GraphQL member name suitable for mutation variables, or `null` when unknown. */
export function toGraphqlEnumName<T extends GraphqlEnumDef>(
  value: unknown,
  enumObj: T,
): GraphqlEnumName<T> | null {
  const name = parseGraphqlEnumName(value, enumObj);
  if (!name || !(name in enumObj)) return null;
  return name;
}

/** `<Select>` options — `value` is always the GraphQL name (key), never the DB int. */
export function graphqlEnumOptions<T extends GraphqlEnumDef>(
  enumObj: T,
  catalog?: string,
): { value: GraphqlEnumName<T>; label: string }[] {
  return graphqlEnumNames(enumObj).map((name) => ({
    value: name,
    label: formatEnum(name, catalog),
  }));
}
